import { ColorsType, ThemeType, styled } from '@openeducation/oe-it-themes';
import { useRef } from 'react';
import { Dimensions, StyleProp, View, ViewStyle } from 'react-native';
import { useHover } from 'react-native-web-hooks';

import { CheckIcon } from '../icons';

export interface RadioButtonProps {
  selected?: boolean;
  style?: StyleProp<ViewStyle>;
  hoveredApplied?: boolean;
  onPress?: () => void;
  type?: 'primary' | 'secondary';
  size?: 'small' | 'large';
}

export const RadioButton = ({
  selected,
  style,
  hoveredApplied,
  onPress,
  type = 'primary',
  size = 'small',
}: RadioButtonProps) => {
  const radioRef = useRef<View>(null);
  const isHovered = useHover(radioRef);

  return (
    <Radio
      onPress={onPress}
      style={style}
      selected={selected}
      ref={radioRef}
      hovered={hoveredApplied || isHovered}
      type={type}
      size={size}
    >
      {selected && <CheckIcon width={size === 'small' ? 8 : 14} />}
    </Radio>
  );
};

const Radio = styled.Pressable<{
  hovered: boolean;
  selected?: boolean;
  type: 'primary' | 'secondary';
  size: 'small' | 'large';
}>`
  border-radius: ${Dimensions.get('window').width / 2}px;
  width: ${({ size }) => (size === 'small' ? '16px' : '24px')};
  height: ${({ size }) => (size === 'small' ? '16px' : '24px')};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(arg) => getBackgroundColor(arg)};
  ${(arg) => getBorder(arg)}
`;

const colorLibrary: { [key: string]: { [key: string]: keyof ColorsType } } = {
  primary: {
    selected: 'primary',
    hovered: 'lightPrimary',
    default: 'backgroundOeBlue20',
  },
  secondary: {
    selected: 'sunnyOrangeLight',
    hovered: 'sunnyOrangeLight',
    default: 'accent',
  },
  base: {
    default: 'border',
  },
};

const getBackgroundColor = ({
  theme,
  hovered,
  selected,
  type,
}: {
  theme: ThemeType;
  hovered: boolean;
  selected?: boolean;
  type: 'primary' | 'secondary';
}) => {
  if (selected) {
    return theme.color[colorLibrary[type].selected];
  }

  if (hovered) {
    return theme.color[colorLibrary[type].hovered];
  }

  return theme.color.white;
};

const getBorder = ({
  theme,
  hovered,
  selected,
  type,
}: {
  theme: ThemeType;
  hovered: boolean;
  selected?: boolean;
  type: 'primary' | 'secondary';
}) => {
  if (selected) {
    return;
  }

  if (hovered) {
    return `border: 4px solid ${theme.color[colorLibrary[type].default]}`;
  }

  return `border: 1px solid ${theme.color[colorLibrary.base.default]}`;
};
